import vertexSrc from '../../shaders/presets/shared/vertex.glsl'

/**
 * Abstract base for all audio-reactive WebGL presets.
 * Subclasses implement only init(canvas) — everything else lives here.
 */
export default class BasePreset {
    constructor() {
        this.id      = ''
        this.name    = ''
        this.plan    = 'free'

        this.gl       = null
        this.uniforms = {}

        this._program    = null
        this._quadBuffer = null
        this._config     = {}
    }

    // ── Config API ─────────────────────────────────────────────────────────────

    getSchema(_plan) { return [] }

    setConfig(cfg) { this._config = { ...this._config, ...cfg } }

    getConfig() { return this._config }

    _applyConfigUniforms() {}

    _hexToRgb(hex) {
        const r = parseInt(hex.slice(1, 3), 16) / 255
        const g = parseInt(hex.slice(3, 5), 16) / 255
        const b = parseInt(hex.slice(5, 7), 16) / 255
        return [r, g, b]
    }

    /**
     * @param {HTMLCanvasElement} canvas
     * @param {string} fragSrc  — GLSL fragment shader source (passed by subclass)
     */
    _setup(canvas, fragSrc) {
        const gl = canvas.getContext('webgl', { antialias: false, alpha: false, preserveDrawingBuffer: true })
        if (!gl) return
        this.gl = gl

        this._program    = this._buildProgram(gl, fragSrc)
        this._quadBuffer = this._setupQuad(gl, this._program)
        gl.useProgram(this._program)

        this.uniforms = {
            uTime:       gl.getUniformLocation(this._program, 'uTime'),
            uHover:      gl.getUniformLocation(this._program, 'uHover'),
            uResolution: gl.getUniformLocation(this._program, 'uResolution'),
            uAudioLevel: gl.getUniformLocation(this._program, 'uAudioLevel'),
            uBass:       gl.getUniformLocation(this._program, 'uBass'),
            uMid:        gl.getUniformLocation(this._program, 'uMid'),
            uTreble:     gl.getUniformLocation(this._program, 'uTreble'),
        }
    }

    /**
     * Must be called by subclass init(canvas):
     *   init(canvas) { this._setup(canvas, fragSrc) }
     */
    init(_canvas) {}

    /**
     * @param {{time: number, audioLevel?: number, bass?: number, mid?: number, treble?: number, hover?: number}} params
     */
    tick({ time, audioLevel = 0, bass = 0, mid = 0, treble = 0, hover = 0 }) {
        const gl = this.gl
        if (!gl) return

        const { width, height } = gl.canvas
        gl.viewport(0, 0, width, height)
        gl.clearColor(0, 0, 0, 1)
        gl.clear(gl.COLOR_BUFFER_BIT)

        const u = this.uniforms
        gl.uniform2f(u.uResolution, width, height)
        gl.uniform1f(u.uTime,       time)
        gl.uniform1f(u.uHover,      hover)
        gl.uniform1f(u.uAudioLevel, audioLevel)
        gl.uniform1f(u.uBass,       bass)
        gl.uniform1f(u.uMid,        mid)
        gl.uniform1f(u.uTreble,     treble)

        this._applyConfigUniforms()

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }

    destroy() {
        const gl = this.gl
        if (!gl) return
        gl.deleteProgram(this._program)
        gl.deleteBuffer(this._quadBuffer)
        gl.getExtension('WEBGL_lose_context')?.loseContext()
        this.gl       = null
        this._program = null
        this._quadBuffer = null
    }

    // ── private helpers ────────────────────────────────────────────────────────

    _compile(gl, type, src) {
        const shader = gl.createShader(type)
        gl.shaderSource(shader, src)
        gl.compileShader(shader)
        return shader
    }

    _buildProgram(gl, fragSrc) {
        const vert = this._compile(gl, gl.VERTEX_SHADER,   vertexSrc)
        const frag = this._compile(gl, gl.FRAGMENT_SHADER, fragSrc)

        if (!gl.getShaderParameter(vert, gl.COMPILE_STATUS))
            console.error('[BasePreset] vertex shader error:', gl.getShaderInfoLog(vert))
        if (!gl.getShaderParameter(frag, gl.COMPILE_STATUS))
            console.error('[BasePreset] fragment shader error:', gl.getShaderInfoLog(frag))

        const prog = gl.createProgram()
        gl.attachShader(prog, vert)
        gl.attachShader(prog, frag)
        gl.linkProgram(prog)

        if (!gl.getProgramParameter(prog, gl.LINK_STATUS))
            console.error('[BasePreset] program link error:', gl.getProgramInfoLog(prog))

        gl.deleteShader(vert)
        gl.deleteShader(frag)
        return prog
    }

    _setupQuad(gl, program) {
        const verts = new Float32Array([-1, -1,  1, -1,  -1,  1,  1,  1])
        const buf   = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, buf)
        gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW)
        const loc = gl.getAttribLocation(program, 'a_position')
        gl.enableVertexAttribArray(loc)
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
        return buf
    }
}
